// tippi/src/pages/admin/settings/AdminTerms.js
import React, { useState, useEffect, useRef } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useTerms } from '../../../hooks/useTerms';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { normalizeTerms } from '../../../utils/terms';
import { COLORS, SHADOWS } from '../../../styles/tokens';

const inputStyle = {
  width: '100%', padding: '9px 10px', borderRadius: 7,
  border: `1.5px solid ${COLORS.border}`, fontSize: 14, boxSizing: 'border-box',
  background: COLORS.white, color: COLORS.textDark,
};

function Field({ label, error, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: COLORS.textDark, marginBottom: 6 }}>
        {label}
        {children}
      </label>
      {error && <p style={{ margin: '4px 0 0', fontSize: 12, color: COLORS.danger }}>{error}</p>}
    </div>
  );
}

// Tallfelt som holder rå tekst i utkastet, så admin kan tømme feltet mens hen skriver
function NumField({ label, value, onChange, error }) {
  return (
    <Field label={label} error={error}>
      <input
        type="number" value={value} onChange={e => onChange(e.target.value)}
        style={{ ...inputStyle, marginTop: 6 }}
      />
    </Field>
  );
}

const toDraft = (t) => ({
  freeMinutes: String(t.freeMinutes),
  waitingRatePerHour: String(t.waitingRatePerHour),
  waitingStepMinutes: String(t.waitingStepMinutes),
  bomturPct: String(t.bomtur.pct),
  bomturMin: String(t.bomtur.minAmount),
  ladder: t.cancellationLadder.map(s => ({ hoursBefore: String(s.hoursBefore), pct: String(s.pct) })),
  confirmed: t.confirmed === true,
});

// Tom streng regnes som ugyldig (Number('') er 0)
const parse = (s) => (String(s).trim() === '' ? NaN : Number(s));
const PCT_ERR = 'Prosent må være mellom 0 og 100.';

function validate(d) {
  const e = { ladder: d.ladder.map(() => ({})) };
  let ok = true;
  const fail = (key, msg) => { e[key] = msg; ok = false; };

  const free = parse(d.freeMinutes);
  if (!(free >= 0 && free <= 600)) fail('freeMinutes', 'Fri lastetid må være mellom 0 og 600 minutter.');
  else if (!Number.isInteger(free)) fail('freeMinutes', 'Fri lastetid må være et helt antall minutter.');
  const rate = parse(d.waitingRatePerHour);
  if (!(rate >= 0 && rate <= 100000)) fail('waitingRatePerHour', 'Timesats må være mellom 0 og 100 000 kr.');
  const step = parse(d.waitingStepMinutes);
  if (!(step > 0 && step <= 240)) fail('waitingStepMinutes', 'Ventetrinn må være større enn 0.');
  else if (!Number.isInteger(step)) fail('waitingStepMinutes', 'Ventetrinn må være et helt antall minutter.');
  const pct = parse(d.bomturPct);
  if (!(pct >= 0 && pct <= 100)) fail('bomturPct', PCT_ERR);
  const min = parse(d.bomturMin);
  if (!(min >= 0 && min <= 1000000)) fail('bomturMin', 'Minstebeløp må være 0 eller mer.');

  if (d.ladder.length === 0) fail('ladderGeneral', 'Trappen må ha minst ett trinn.');
  d.ladder.forEach((s, i) => {
    const h = parse(s.hoursBefore);
    if (!(h >= 0)) { e.ladder[i].hoursBefore = 'Timer må være 0 eller mer.'; ok = false; }
    const p = parse(s.pct);
    if (!(p >= 0 && p <= 100)) { e.ladder[i].pct = PCT_ERR; ok = false; }
  });
  const hours = d.ladder.map(s => parse(s.hoursBefore)).filter(Number.isFinite);
  if (new Set(hours).size !== hours.length) fail('ladderGeneral', 'Hvert trinn må ha ulikt antall timer.');
  return { ok, errors: e };
}

// Versjonen øker bare når selve satsene endres, ikke når bare «bekreftet» endres.
// Ny aksept fra bestillerne kreves bare ved endrede satser.
const RATE_KEYS = ['freeMinutes', 'waitingRatePerHour', 'waitingStepMinutes', 'bomtur', 'cancellationLadder'];
const ratesChanged = (a, b) => RATE_KEYS.some(k => JSON.stringify(a[k]) !== JSON.stringify(b[k]));

export default function AdminTerms() {
  const { terms: stored, loading } = useTerms();
  const isMobile = useIsMobile();
  const [draft, setDraft] = useState(() => toDraft(stored));
  const [errors, setErrors] = useState(null);
  const [status, setStatus] = useState(null); // null | 'saving' | 'ok' | 'err'

  // Utkastet følger lagret dokument til admin har begynt å redigere
  const dirty = useRef(false);
  useEffect(() => {
    if (dirty.current) return;
    setDraft(toDraft(stored));
  }, [stored]);

  function edit(fn) { dirty.current = true; setDraft(fn); setStatus(null); }
  const set = (key) => (value) => edit(d => ({ ...d, [key]: value }));
  const setStep = (i, key) => (value) => edit(d => ({
    ...d, ladder: d.ladder.map((s, idx) => (idx === i ? { ...s, [key]: value } : s)),
  }));
  const addStep = () => edit(d => ({ ...d, ladder: [...d.ladder, { hoursBefore: '', pct: '' }] }));
  const removeStep = (i) => edit(d => (d.ladder.length <= 1 ? d : { ...d, ladder: d.ladder.filter((_, idx) => idx !== i) }));

  async function handleSave() {
    const { ok, errors: errs } = validate(draft);
    setErrors(errs);
    if (!ok) { setStatus(null); return; }
    const next = normalizeTerms({
      freeMinutes: parse(draft.freeMinutes),
      waitingRatePerHour: parse(draft.waitingRatePerHour),
      waitingStepMinutes: parse(draft.waitingStepMinutes),
      bomtur: { pct: parse(draft.bomturPct), minAmount: parse(draft.bomturMin) },
      cancellationLadder: draft.ladder.map(s => ({ hoursBefore: parse(s.hoursBefore), pct: parse(s.pct) })),
      confirmed: draft.confirmed,
      version: stored.version,
    });
    if (ratesChanged(next, stored)) next.version = stored.version + 1;
    setStatus('saving');
    try {
      await setDoc(doc(db, 'settings', 'terms'), next);
      dirty.current = false;
      setStatus('ok');
    } catch (err) {
      console.error('AdminTerms save:', err);
      setStatus('err');
    }
  }

  const e = errors ?? { ladder: [] };
  const grid = { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 };

  return (
    <div style={{ maxWidth: 720 }}>
      <h1 style={{ margin: '0 0 4px', fontSize: 22, color: COLORS.textDark }}>Vilkår</h1>
      <p style={{ margin: '0 0 20px', fontSize: 13, color: COLORS.textMid }}>
        Satser for ventetid, bomtur og avbestilling. Versjon {stored.version}.
        {!stored.confirmed && (
          <span style={{
            marginLeft: 8, padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 600,
            background: COLORS.warningBg, color: COLORS.warningText,
          }}>Ikke bekreftet</span>
        )}
      </p>

      <div style={{ background: COLORS.white, borderRadius: 12, boxShadow: SHADOWS.sm, padding: 24, marginBottom: 20 }}>
        <h2 style={{ margin: '0 0 16px', fontSize: 16, color: COLORS.textDark }}>Ventetid og bomtur</h2>
        <div style={grid}>
          <NumField label="Fri lastetid (minutter)" value={draft.freeMinutes} onChange={set('freeMinutes')} error={e.freeMinutes} />
          <NumField label="Timesats ventetid (kr)" value={draft.waitingRatePerHour} onChange={set('waitingRatePerHour')} error={e.waitingRatePerHour} />
          <NumField label="Ventetrinn (minutter)" value={draft.waitingStepMinutes} onChange={set('waitingStepMinutes')} error={e.waitingStepMinutes} />
          <span />
          <NumField label="Bomtur (% av oppdragsprisen)" value={draft.bomturPct} onChange={set('bomturPct')} error={e.bomturPct} />
          <NumField label="Bomtur minstebeløp (kr)" value={draft.bomturMin} onChange={set('bomturMin')} error={e.bomturMin} />
        </div>
      </div>

      <div style={{ background: COLORS.white, borderRadius: 12, boxShadow: SHADOWS.sm, padding: 24, marginBottom: 20 }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 16, color: COLORS.textDark }}>Avbestillingstrapp</h2>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: COLORS.textMid }}>
          Gjelder fra og med antall timer før planlagt tid. Et trinn med 0 timer gjelder når tiden er nær eller passert.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {draft.ladder.map((s, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr auto', gap: 12, alignItems: 'start' }}>
              <NumField label={`Trinn ${i + 1}: timer før`} value={s.hoursBefore} onChange={setStep(i, 'hoursBefore')} error={e.ladder[i]?.hoursBefore} />
              <NumField label={`Trinn ${i + 1}: prosent`} value={s.pct} onChange={setStep(i, 'pct')} error={e.ladder[i]?.pct} />
              <button
                type="button" aria-label={`Fjern trinn ${i + 1}`} disabled={draft.ladder.length <= 1}
                onClick={() => removeStep(i)}
                style={{
                  marginTop: 24, padding: '9px 12px', borderRadius: 7, border: `1.5px solid ${COLORS.border}`,
                  background: COLORS.white, color: COLORS.textDark, cursor: draft.ladder.length <= 1 ? 'default' : 'pointer',
                  opacity: draft.ladder.length <= 1 ? 0.5 : 1,
                }}
              >Fjern</button>
            </div>
          ))}
        </div>
        {e.ladderGeneral && <p style={{ margin: '8px 0 0', fontSize: 12, color: COLORS.danger }}>{e.ladderGeneral}</p>}
        <button
          type="button" onClick={addStep}
          style={{
            marginTop: 12, padding: '8px 14px', borderRadius: 7, border: `1.5px solid ${COLORS.border}`,
            background: COLORS.white, color: COLORS.textDark, fontWeight: 600, cursor: 'pointer',
          }}
        >Legg til trinn</button>
      </div>

      <div style={{ background: COLORS.white, borderRadius: 12, boxShadow: SHADOWS.sm, padding: 24 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: COLORS.textDark }}>
          <input
            type="checkbox" checked={draft.confirmed}
            onChange={ev => edit(d => ({ ...d, confirmed: ev.target.checked }))}
          />
          Tallene er bekreftet
        </label>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, marginTop: 20 }}>
          {status === 'ok' && <span style={{ fontSize: 13, color: COLORS.success }}>✓ Lagret</span>}
          {status === 'err' && <span style={{ fontSize: 13, color: COLORS.danger }}>Kunne ikke lagre</span>}
          <button
            type="button" onClick={handleSave} disabled={loading || status === 'saving'}
            style={{
              padding: '9px 20px', borderRadius: 8, border: 'none', background: COLORS.primary,
              color: COLORS.onPrimary, fontWeight: 600, fontSize: 14, cursor: 'pointer',
            }}
          >Lagre</button>
        </div>
      </div>
    </div>
  );
}
